import { PrimaryButton } from "@digipost/dds-buttons-react";
import { Checkbox } from "@digipost/dds-checkbox-react";
import { Input } from "@digipost/dds-input-react";
import { useLanguage } from "@digipost/translate";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie";
import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { ValidationRules } from "../../../types/entrypoint";
import { Link } from "../../../types/link";
import { ChildRegistration } from "../../../types/registration";
import ErrorPage from "../ErrorPage";
import InfoWarningText from "../InfoWarningText";
import { getJSON } from "../infrastructure/digipost-fetch";
import { Dispatch } from "../infrastructure/redux/actions";
import {
  organisationChildAction,
  RegistrationStatus,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useTranslate } from "../infrastructure/useTranslate";

type Organisation = {
  link: Link[];
  organisationAgreementId: number;
  senderId: number;
  name: string;
  organisationNumber: string;
  customerNumber: string;
  digipostaddress: string;
  hasLogo: boolean;
  hasCertificate: boolean;
  environment: string;
  usesExternalId: boolean;
  organisationPart: string;
  parentName: string;
  comment: string;
  printAppOnline: boolean;
};

const buildEntryOrganisationUri = (accountId: string): string =>
  `/post/api/org/${accountId}`;

const getOrganisationState = async (
  accountId: string,
): Promise<Organisation> => {
  const json = await getJSON<Organisation>(
    buildEntryOrganisationUri(accountId),
    "/",
  );
  return Promise.resolve(json);
};

export const UnderkontoSkjema: FC = () => {
  const [parentOrg, setParentOrg] = useState<Organisation>();

  const [organisationNumber, setOrganisationNumber] = useState<string>("");
  const [telephoneNumber, setTelephoneNumber] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [organisationPart, setOrganisationPart] = useState<string>("");
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);

  const [offentlig, setOffentlig] = useState(false);
  const [showRegisterError, setShowRegisterError] = useState(false);
  const [invalidInputs, setInvalidInputs] = useState<string[]>([]);
  const [errorText, setErrorText] = useState("");

  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );

  const translate = useTranslate();
  const dispatch = useDispatch<Dispatch>();
  const { language } = useLanguage();
  const { accountId } = useParams<{ accountId: string }>();

  const fetchOrganisationState = useCallback(async () => {
    if (!accountId) return;

    const data = await getOrganisationState(accountId);
    setParentOrg(data);

    if (!data) {
      setErrorText(translate("register.org.children.error_occurred"));
    }

    const DIFI_ORGNUMBER = "991825827";
    const isOffentlig = data.organisationNumber === DIFI_ORGNUMBER;
    setOffentlig(isOffentlig);
  }, [accountId, translate]);

  useEffect(() => {
    fetchOrganisationState();
  }, [fetchOrganisationState]);

  useEffect(() => {
    document.title = `Digipost - ${translate("register.org.children.header")}`;
  }, [translate]);

  const toState = (
    event: React.ChangeEvent<HTMLInputElement>,
    setState: React.Dispatch<React.SetStateAction<string>>,
  ): void => {
    setInvalidInputs(invalidInputs.filter((str) => str === event.target.name));
    setState(event.target.value);
  };

  const isValidFormValue = (
    value: string,
    required: boolean,
    type: keyof ValidationRules,
  ): boolean => {
    const validationRule = validationRules[type];
    const emptyAndRequired = !value && required;
    return (
      !emptyAndRequired &&
      (!validationRule || !value || new RegExp(validationRule).test(value))
    );
  };

  const validate = (): boolean => {
    setShowRegisterError(false);
    let valid = true;
    const newInvalidInputs = [];
    if (!isTermsAccepted) {
      setShowRegisterError(true);
      valid = false;
    }
    if (!isValidFormValue(organisationNumber, true, "organisationNumber")) {
      newInvalidInputs.push("organisationNumber");
    }
    if (!(name && name.length > 0)) {
      newInvalidInputs.push("name");
    }
    if (!isValidFormValue(email, true, "email")) {
      newInvalidInputs.push("email");
    }
    if (!isValidFormValue(telephoneNumber, true, "phoneNumber")) {
      newInvalidInputs.push("telephoneNumber");
    }
    setInvalidInputs(newInvalidInputs);
    return valid && newInvalidInputs.length === 0;
  };

  const submit = (): void => {
    const valid = validate();
    if (valid && parentOrg) {
      let formData: ChildRegistration = {
        organisationNumber: organisationNumber,
        email: email,
        telephoneNumber: telephoneNumber,
        language: language,
        name: name,
        parentId: parentOrg.senderId,
        registrationContext: "digipost",
      };
      if (offentlig) {
        formData = {
          ...formData,
          organisationPart: organisationPart,
        };
      }
      dispatch(
        organisationChildAction(
          formData,
          parentOrg.link,
          (newAccountId: string) =>
            Cookies.set("digipost.org.children.new", newAccountId, {
              path: "/",
              expires: 1,
              secure: true,
            }),
        ),
      );
    }
  };

  const errorElement = (text: string): ReactElement => (
    <ErrorPage>
      <p>{text}</p>
      <a href={`/${language === "nb" ? "" : language}`}>
        <Translated textKey={"generic.back_to_front_page"} />
      </a>
    </ErrorPage>
  );

  const valid = (field: string): boolean => !invalidInputs.includes(field);

  if (errorText !== "") {
    return errorElement(errorText);
  }

  const approveButtonTextKey = offentlig
    ? "generic.create"
    : "register.org.children.send_to_approve";

  return (
    <div>
      <div className="register-content-wrapper">
        <h1 className="dds-title-type">
          <Translated textKey="register.org.children.header" />
        </h1>
      </div>

      <div className="register-content">
        <p className={"dds-body-type"}>
          <Translated
            textKey={"register.org.children.info"}
            params={{ orgName: parentOrg?.name }}
          />
        </p>

        <Input
          name="organisationNumber"
          valid={valid("organisationNumber")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !valid("organisationNumber")
              ? translate("org.info.invalid_organisation_number")
              : undefined
          }
          onChange={(event) => toState(event, setOrganisationNumber)}
          labelText={`${translate("generic.organisation_number")}*`}
        />
        <Input
          name="name"
          valid={valid("name")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !valid("name")
              ? translate("org.info.name_required_validation")
              : undefined
          }
          onChange={(event) => toState(event, setName)}
          labelText={`${translate("org.info.company_name")}*`}
        />

        {offentlig && (
          <Input
            name="organisationPart"
            onChange={(event) => toState(event, setOrganisationPart)}
            labelText={translate("register.org.children.organisation_part")}
          />
        )}

        <Input
          name="email"
          valid={valid("email")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !valid("email")
              ? translate("register.org.registration_form.invalid_email")
              : undefined
          }
          onChange={(event) => toState(event, setEmail)}
          labelText={`${translate("register.org.registration_form.email")}*`}
        />
        <Input
          name="telephoneNumber"
          valid={valid("telephoneNumber")}
          errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
          errorText={
            !valid("telephoneNumber")
              ? translate("register.org.registration_form.invalid_phone")
              : undefined
          }
          onChange={(event) => toState(event, setTelephoneNumber)}
          inputType="phone"
          labelText={`${translate("register.org.registration_form.phone")}*`}
        />

        <div className="extra-margin-bottom">
          <Checkbox
            checked={isTermsAccepted}
            onChange={() => setIsTermsAccepted(!isTermsAccepted)}
            className="green"
          >
            <Translated
              textKey="register.org.intro.accept_terms"
              allowHtml={true}
            />
          </Checkbox>
          {showRegisterError && (
            <InfoWarningText textkey="register.org.intro.must_accept_terms" />
          )}
        </div>
        {registrationStatus === RegistrationStatus.FAILED && (
          <InfoWarningText red={true} textkey="generic.error" />
        )}
        <div>
          <PrimaryButton id="til_godkjenning" onClick={submit}>
            <Translated textKey={approveButtonTextKey} />
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
